import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { IonicModule, PopoverController } from '@ionic/angular';
import { EMPTY, Subject, Subscription, asyncScheduler, from, merge, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  first,
  map,
  skip,
  takeUntil,
  throttleTime,
  timeout,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { DialogComponent, TitleContentComponent, ToastFacade } from 'ngx-zcs-components';
import { CallPROSolarmanLastActionResponse, InverterUpgradeConfig } from 'ngx-modbus';

import { createDebugLogger, DebugConfig } from '../../shared/debug';
import { UpgradeCommonLabels } from '../../shared/labels/upgrade-labels';
import { isLoggerUpgrading$ } from '../upgrade-inverter.utils';
import { OneClickLabels } from './one-click.labels';
import { OneClickUpgradeService } from './one-click-upgrade.service';
import { selectOneClickPushState } from './one-click.selectors';
import {
  OneClickCurrentVersions,
  OneClickGate,
  OneClickModulePlan,
  OneClickPushState,
  buildOneClickModulePlan,
  resolveArmUpgrade,
} from './one-click.utils';

const DEBUG_CONFIG: DebugConfig = { ENABLED: false, LOG_API: true, LOG_STATE: true, LOG_DATA: true };
const Logger = createDebugLogger('[OneClickPanel]', DEBUG_CONFIG);

const ARM_UPDATE_TIMEOUT_MS = 15 * 60 * 1000;
// cadence of the support-types re-check while waiting for the ARM update
const ARM_RECHECK_INTERVAL_MS = 30 * 1000;
// anti rate-limit guard between push-triggered support-types calls
const SUPPORT_TYPES_GUARD_MS = 2 * 1000;
// Grace window after the launch while the subscription has not pushed
// `updating: true` yet (it usually takes 3-4 min): the optimistic marker keeps
// the progress view alive across reloads only inside this window. Closing the
// update is the cloud's job (terminal push or lastAction arbitration), never a
// local timer.
const ONE_CLICK_UPDATING_PUSH_GRACE_MS = 10 * 60 * 1000;
const ONE_CLICK_STORAGE_KEY = (sn: string) => `oneClickUpdateTime_${sn.toLowerCase()}`;
// optimistic marker of the ARM-first step (same-browser reload resume; TTL = ARM_UPDATE_TIMEOUT_MS)
const ARM_FIRST_STORAGE_KEY = (sn: string) => `oneClickArmFirstTime_${sn.toLowerCase()}`;
// grace after a FOTA cycle ends with the ARM unchanged before declaring failure:
// covers the AWS version-report lag after the post-flash reboot, and gives
// support-types (the success authority) time to confirm instead.
const ARM_UNCHANGED_FAIL_GRACE_MS = 3 * 60 * 1000;
// pull fallback (Solarman lastAction) during the one-click: poll cadence and
// how long the push channel must stay silent before consulting it
const ONE_CLICK_LASTACTION_POLL_MS = 60 * 1000;
const ONE_CLICK_PUSH_SILENCE_MS = 90 * 1000;
// upd_global terminal statuses: 7 complete, 2 downloadFailed, 4 uploadError, 6 verifyError.
// With updating=false they are leftovers of a PREVIOUS run (the cloud never clears
// upd_global) and must be ignored until the current update genuinely starts.
const TERMINAL_UPD_STATUSES = [2, 4, 6, 7];

// Identity of a push snapshot: a change in any of these fields is a genuine
// transition. The raw selector re-emits a new object on EVERY system-state
// mutation (telemetry included) — timing logic (push-silence detection) must
// only ever see genuine transitions.
const pushSnapshotKey = (s: OneClickPushState): string =>
  `${s.arm}|${s.updating}|${s.fotaStatus}|${s.updGlobal?.status}|${s.updGlobal?.progress}|${s.updGlobal?.createdAt}`;

type OneClickPhase =
  | 'resolving'
  | 'armFirst'
  | 'armUpdating'
  | 'ready'
  | 'updating'
  | 'success'
  | 'failed'
  | 'blocked'
  | 'unknown';

type PanelLabels = OneClickLabels &
  Pick<UpgradeCommonLabels, 'confirm' | 'cancel' | 'confirmTitle' | 'module' | 'currentVersion' | 'latestVersion'> &
  Record<string, string>;

@Component({
  selector: 'lib-one-click-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonicModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
    TitleContentComponent,
  ],
  templateUrl: './one-click-panel.component.html',
  styleUrls: ['./one-click-panel.component.scss'],
})
export class OneClickPanelComponent implements OnInit, OnDestroy {
  @Input() uiFramework: 'material' | 'ionic' = 'material';
  @Input() labels!: PanelLabels;
  /** Inverter serial (store match + display). */
  @Input() inverterSn!: string;
  /** Logger serial for the mutation loggerId. */
  @Input() loggerId!: string;
  /** Inverter serial for the mutation inverterId / deviceSN. */
  @Input() inverterId!: string;
  /** Solarman numeric deviceId of the inverter (support-types re-check). */
  @Input() solarmanDeviceId?: string | number | null;
  @Input() gate!: OneClickGate;
  @Input() config?: InverterUpgradeConfig | null;
  @Input() currentVersions: OneClickCurrentVersions = {};
  @Output() refreshRequested = new EventEmitter<void>();
  /**
   * Mirrors the in-flight phases (armUpdating/updating) to the parent: only it
   * knows the InverterStatusFacade (this folder must stay droppable), and
   * config must stay blocked while the device is flashing.
   */
  @Output() upgradeInFlight = new EventEmitter<boolean>();

  /**
   * 'resolving' is the neutral boot state (spinner): 'unknown' must appear
   * ONLY on a genuine support-types failure, never as a transient during init.
   */
  phase: OneClickPhase = 'resolving';
  armPlan: OneClickModulePlan | null = null;
  modulePlan: OneClickModulePlan[] | null = null;
  progressStatus: number | null = null;
  progressValue = 0;
  busy = false;

  private readonly destroy$ = new Subject<void>();
  private progressSub: Subscription | null = null;
  /** FOTA-based outcome watcher of the ARM-first step (fota 1→0 + ARM unchanged = failed). */
  private armFotaSub: Subscription | null = null;
  private armFailGraceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pull fallback (Solarman lastAction) while the push channel is silent. */
  private lastActionPollSub: Subscription | null = null;
  /** Epoch ms of the launch of the current update (mirror of the optimistic flag). */
  private updateStartTs: number | null = null;
  /** Last upgradeInFlight value emitted — dedup of the phase→in-flight mapping. */
  private lastInFlight = false;

  constructor(
    private readonly oneClickUpgradeService: OneClickUpgradeService,
    private readonly store: Store,
    private readonly toastFacade: ToastFacade,
    private readonly dialog: MatDialog,
    private readonly popoverController: PopoverController,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.resolveInitialPhase();
    this.watchExternalFota();
  }

  /**
   * An update may be started elsewhere (another session/tab) or before the
   * store had any snapshot (seed gap) while this panel sits idle on an
   * actionable CTA. Two distinct external signals must take over the view:
   *  - `updating: true`: a full one-click in flight — the api path signals the
   *    flight ONLY here (a upd_global alone, terminal or not, is residue of a
   *    previous run) → progress view;
   *  - fota_status '1': ARM-first FOTA in flight — the iot path signals ONLY
   *    here (updating/upd_global stay null) → armUpdating view.
   * Never leave "Update ARM" / "Update all modules" clickable on a device
   * that is already flashing.
   */
  private watchExternalFota(): void {
    this.store.select(selectOneClickPushState(this.inverterSn))
      .pipe(
        filter((s): s is OneClickPushState => !!s),
        takeUntil(this.destroy$),
      )
      .subscribe((s) => {
        if (this.phase !== 'armFirst' && this.phase !== 'ready') {
          return;
        }
        if (this.isActiveUpdate(s)) {
          Logger.state('external one-click detected — switching to updating');
          this.progressStatus = s.updGlobal?.status ?? null;
          this.progressValue = s.updGlobal?.progress ?? 0;
          this.beginUpdatingPhase();
        } else if (s.fotaStatus === '1') {
          Logger.state('external FOTA detected — switching to armUpdating');
          this.beginArmUpdatingPhase();
        }
      });
  }

  /**
   * Decide the initial phase. If an update is already in flight (page reloaded
   * mid-update: optimistic flag, or cloud push says `updating: true` on
   * the inverter), resume the progress view instead of falling back to the gate
   * — support-types keeps returning COMBINATION during the update, so the gate
   * would otherwise (wrongly) show `ready` with the CTA.
   *
   * The decision is taken on the CURRENT store snapshot (sync emission): the
   * seed arrives without inverter[] (resolver gap, see doc §3), so *waiting*
   * for a snapshot would burn a probe timeout on every page entry while the
   * template shows a bogus state.
   */
  private resolveInitialPhase(): void {
    this.store.select(selectOneClickPushState(this.inverterSn))
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe((s) => {
        if (this.hasActiveOptimistic()) {
          // Never trust the optimistic flag blindly: if the store already
          // holds the terminal outcome of THIS run (push arrived before or
          // while reloading), close immediately instead of showing a progress
          // bar over a finished update.
          if (s && this.isCurrentRunTerminal(s)) {
            this.progressStatus = s.updGlobal?.status ?? null;
            this.progressValue = s.updGlobal?.progress ?? 0;
            this.finishOneClick(s.updGlobal?.status === 7 ? 'success' : 'failed');
            return;
          }
          this.beginUpdatingPhase();
          return;
        }
        if (this.hasActiveArmOptimistic()) {
          this.beginArmUpdatingPhase();
          return;
        }
        if (s && this.isActiveUpdate(s)) {
          this.progressStatus = s.updGlobal?.status ?? null;
          this.progressValue = s.updGlobal?.progress ?? 0;
          this.beginUpdatingPhase();
        } else if (s?.fotaStatus === '1') {
          // legacy/iot FOTA in flight (ARM-first launched before this page
          // load or from another session): resume the waiting view.
          this.beginArmUpdatingPhase();
        } else {
          this.enterFromGate();
        }
      });
  }

  private isActiveUpdate(s: OneClickPushState | null): boolean {
    // The api path signals an update in flight ONLY via `updating`: any
    // upd_global with updating=false — terminal or not — is a leftover of a
    // previous run (the cloud never clears upd_global, L8).
    return !!s?.updating;
  }

  private getOptimisticStartTs(): number | null {
    const raw = localStorage.getItem(ONE_CLICK_STORAGE_KEY(this.inverterSn));
    const ts = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(ts) ? ts : null;
  }

  private hasActiveOptimistic(): boolean {
    const ts = this.getOptimisticStartTs();
    return ts != null && new Date().getTime() - ts < ONE_CLICK_UPDATING_PUSH_GRACE_MS;
  }

  private hasActiveArmOptimistic(): boolean {
    const raw = localStorage.getItem(ARM_FIRST_STORAGE_KEY(this.inverterSn));
    const ts = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(ts) && new Date().getTime() - ts < ARM_UPDATE_TIMEOUT_MS;
  }

  /**
   * Terminal upd_global written AFTER the current update was launched
   * (upd_global.createdAt ≥ launch ts) = outcome of THIS run — genuine even if
   * `updating: true` was never observed (fast failures, page reloads). A
   * terminal older than the launch is a leftover of a previous run (the cloud
   * never clears upd_global). `updating: true` always wins as "in flight".
   */
  private isCurrentRunTerminal(s: OneClickPushState): boolean {
    const upd = s.updGlobal;
    if (s.updating || !upd || !TERMINAL_UPD_STATUSES.includes(upd.status)) {
      return false;
    }
    const startTs = this.updateStartTs ?? this.getOptimisticStartTs();
    return startTs != null && (upd.createdAt ?? 0) * 1000 >= startTs;
  }

  private beginUpdatingPhase(): void {
    this.setPhase('updating');
    this.watchOneClickProgress();
  }

  private beginArmUpdatingPhase(): void {
    this.setPhase('armUpdating');
    this.watchArmCompletion();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.progressSub?.unsubscribe();
    this.armFotaSub?.unsubscribe();
    this.lastActionPollSub?.unsubscribe();
    this.clearArmFailGraceTimer();
  }

  get moduleList(): string {
    return (this.modulePlan ?? [])
      .map((m) => (m.module === 'SAFETY' ? `• ${m.module}` : `• ${m.module}: ${m.currentVersion ?? '—'} → ${m.expectedVersion ?? '—'}`))
      .join('\n');
  }

  statusLabel(status: number | null, progress: number): string {
    switch (status) {
      case 0: return this.labels.ahStatusDownloading;
      case 1: return this.labels.ahStatusDownloaded;
      case 2: return this.labels.ahStatusDownloadFailed;
      case 3: return (this.labels.ahStatusUploading ?? '').replace('{progress}', `${progress ?? 0}`);
      case 4: return this.labels.ahStatusUploadError;
      case 5: return this.labels.ahStatusVerified;
      case 6: return this.labels.ahStatusVerifyError;
      case 7: return this.labels.ahStatusComplete;
      default: return this.labels.oneClickUpdating;
    }
  }

  // ─── Actions ──────────────────────────────────────────────────

  async startArmFirst(): Promise<void> {
    if (this.busy || !this.armPlan) {
      return;
    }
    const confirmed = await this.confirm(this.labels.oneClickArmFirstExplain);
    if (!confirmed) {
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    try {
      await this.oneClickUpgradeService.sendArmFirst({
        loggerId: this.loggerId,
        inverterId: this.inverterId,
        armLink: this.armPlan.link,
      });
      // Optimistic marker: a same-browser reload mid-ARM resumes armUpdating
      // instead of re-offering the CTA (the iot path pushes no updating flag).
      localStorage.setItem(ARM_FIRST_STORAGE_KEY(this.inverterSn), `${new Date().getTime()}`);
      this.beginArmUpdatingPhase();
    } catch (error) {
      Logger.error('sendArmFirst failed ' + JSON.stringify({ msg: (error as Error)?.message }));
      this.toast(this.labels.oneClickFailed, 'error');
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  async startOneClick(): Promise<void> {
    if (this.busy || !this.modulePlan) {
      return;
    }
    const confirmed = await this.confirm(this.labels.oneClickConfirmMessage.replace('{moduleList}', this.moduleList));
    if (!confirmed) {
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    try {
      await this.oneClickUpgradeService.sendOneClick({
        loggerId: this.loggerId,
        deviceSN: this.inverterId,
        plan: this.modulePlan,
      });
      this.updateStartTs = new Date().getTime();
      localStorage.setItem(ONE_CLICK_STORAGE_KEY(this.inverterSn), `${this.updateStartTs}`);
      this.setPhase('updating');
      this.watchOneClickProgress();
    } catch (error) {
      Logger.error('sendOneClick failed ' + JSON.stringify({ msg: (error as Error)?.message }));
      this.toast(this.labels.oneClickFailed, 'error');
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  /** Retry from failed/unknown: re-check support-types and route. */
  async retry(): Promise<void> {
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    try {
      if (this.solarmanDeviceId == null) {
        this.enterFromGate();
        return;
      }
      const support = await this.oneClickUpgradeService.getSupportTypes(this.solarmanDeviceId);
      const hasCombination = support.types?.some((t) => t.code === 'COMBINATION') ?? false;
      this.enterResolved(hasCombination ? 'ready' : 'armFirst');
    } catch (error) {
      Logger.warn('retry getSupportTypes failed ' + JSON.stringify({ error: `${error}` }));
      this.setPhase('unknown');
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  // ─── State machine ────────────────────────────────────────────

  private enterFromGate(): void {
    switch (this.gate?.state) {
      case 'ready':
        this.enterResolved('ready');
        break;
      case 'armFirst':
        this.enterResolved('armFirst');
        break;
      default:
        this.setPhase('unknown');
    }
  }

  private enterResolved(target: 'ready' | 'armFirst'): void {
    if (target === 'ready') {
      this.modulePlan = buildOneClickModulePlan(this.config, this.currentVersions);
      this.setPhase(this.modulePlan ? 'ready' : 'blocked');
    } else {
      this.armPlan = resolveArmUpgrade(this.config, this.currentVersions.ARM);
      this.setPhase(this.armPlan ? 'armFirst' : 'blocked');
    }
  }

  private setPhase(phase: OneClickPhase): void {
    this.phase = phase;
    const inFlight = phase === 'armUpdating' || phase === 'updating';
    if (inFlight !== this.lastInFlight) {
      this.lastInFlight = inFlight;
      this.upgradeInFlight.emit(inFlight);
    }
    this.cdr.markForCheck();
  }

  /** After ARM update: re-check support-types until COMBINATION appears. */
  private watchArmCompletion(): void {
    this.watchArmFotaOutcome();
    if (this.solarmanDeviceId == null) {
      return;
    }
    this.progressSub?.unsubscribe();
    // Distill the push stream to the meaningful transitions (ARM version
    // change, fota flips): the raw selector re-emits on EVERY system-state
    // mutation (the projector returns a new object each run), which would hit
    // support-types at the plant's push rate. Distilled events are a handful
    // per update, so they can trigger an immediate re-check behind a short
    // guard instead of a 30s throttle (which the ticker would always claim,
    // degrading the push channel to a fixed poll).
    const meaningfulPush$ = this.store.select(selectOneClickPushState(this.inverterSn)).pipe(
      filter((s): s is OneClickPushState => !!s),
      map((s) => `${s.arm}|${s.fotaStatus}`),
      distinctUntilChanged(),
      // the snapshot already in the store at watch start is not a transition;
      // the immediate first check is the ticker's timer(0)
      skip(1),
    );
    this.progressSub = merge(
      // store push (subscription) → immediate re-check on a transition…
      meaningfulPush$,
      // …the periodic ticker guarantees the re-check even when the
      // subscription stays silent during fota
      timer(0, ARM_RECHECK_INTERVAL_MS),
    )
      .pipe(
        // trailing:true so a push landing inside the guard window is deferred
        // to its close instead of silently dropped
        throttleTime(SUPPORT_TYPES_GUARD_MS, asyncScheduler, { leading: true, trailing: true }),
        // a transient support-types failure must not kill the completion watcher
        exhaustMap(() =>
          from(this.oneClickUpgradeService.getSupportTypes(this.solarmanDeviceId!)).pipe(
            catchError((error) => {
              Logger.warn('watchArmCompletion getSupportTypes failed ' + JSON.stringify({ error: `${error}` }));
              return EMPTY;
            }),
          ),
        ),
        filter((support) => support.types?.some((t) => t.code === 'COMBINATION') ?? false),
        first(),
        timeout(ARM_UPDATE_TIMEOUT_MS),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.clearArmWatch();
          this.toast(this.labels.oneClickSuccess, 'success');
          // COMBINATION appeared because the ARM just reached the flashed
          // version: reflect it as the new current instead of the stale
          // pre-update one (the parent's versions are only re-read on refresh).
          if (this.armPlan?.expectedVersion) {
            this.currentVersions = { ...this.currentVersions, ARM: this.armPlan.expectedVersion };
          }
          this.enterResolved('ready');
        },
        error: () => {
          // soft timeout — invite manual refresh, keep the panel
          this.toast(this.labels.oneClickUpdating, 'success');
        },
      });
  }

  /**
   * Failure detection for the ARM-first step. The device reports the attempt
   * outcome ONLY through fota_status (1 → 0): when the cycle ends and the ARM
   * version is still the one we started from, the flash did not take (e.g.
   * firmware refused device-side). A grace window absorbs the AWS
   * version-report lag after the post-flash reboot — and gives support-types
   * (the success authority, watched in parallel) time to confirm — before
   * exiting to 'failed' with retry instead of waiting forever for a
   * COMBINATION that will never come.
   */
  private watchArmFotaOutcome(): void {
    this.armFotaSub?.unsubscribe();
    let armAtStart: string | null = null;
    let sawFotaActive = false;
    this.armFotaSub = this.store.select(selectOneClickPushState(this.inverterSn))
      .pipe(
        filter((s): s is OneClickPushState => !!s),
        takeUntil(this.destroy$),
      )
      .subscribe((s) => {
        if (armAtStart === null && s.arm) {
          armAtStart = s.arm;
        }
        if (s.fotaStatus === '1') {
          sawFotaActive = true;
          this.clearArmFailGraceTimer();
          return;
        }
        if (!sawFotaActive || s.fotaStatus !== '0') {
          return;
        }
        if (s.arm && armAtStart && s.arm !== armAtStart) {
          // ARM changed: the flash took — the support-types watcher will confirm.
          this.clearArmFailGraceTimer();
          return;
        }
        if (this.armFailGraceTimer == null) {
          Logger.warn('ARM-first FOTA ended with ARM unchanged — arming fail grace ' + JSON.stringify({ arm: s.arm, armAtStart }));
          this.armFailGraceTimer = setTimeout(() => this.failArmFirst(), ARM_UNCHANGED_FAIL_GRACE_MS);
        }
      });
  }

  private failArmFirst(): void {
    this.progressSub?.unsubscribe();
    this.clearArmWatch();
    this.toast(this.labels.oneClickFailed, 'error');
    this.setPhase('failed');
  }

  private clearArmWatch(): void {
    this.armFotaSub?.unsubscribe();
    this.armFotaSub = null;
    this.clearArmFailGraceTimer();
    localStorage.removeItem(ARM_FIRST_STORAGE_KEY(this.inverterSn));
  }

  private clearArmFailGraceTimer(): void {
    if (this.armFailGraceTimer != null) {
      clearTimeout(this.armFailGraceTimer);
      this.armFailGraceTimer = null;
    }
  }

  /**
   * During one-click: reflect upd_global status/progress; terminal → success/failed.
   * No local deadline: closing the update is the cloud's job — terminal push,
   * or the lastAction pull fallback (bounded by the Solarman order's own
   * timeout) when the push channel goes silent.
   */
  private watchOneClickProgress(): void {
    this.progressSub?.unsubscribe();
    this.lastActionPollSub?.unsubscribe();
    let wasUpdating = false;
    let lastPushAt = new Date().getTime();
    this.progressSub = this.store.select(selectOneClickPushState(this.inverterSn))
      .pipe(
        filter((s): s is OneClickPushState => !!s),
        // distill to genuine transitions: without this, lastPushAt refreshes on
        // every store mutation and the silence-based fallback below never fires
        distinctUntilChanged((a, b) => pushSnapshotKey(a) === pushSnapshotKey(b)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (s) => {
          lastPushAt = new Date().getTime();
          // Genuine "in flight" signals: the updating flag, or a terminal
          // upd_global newer than our launch (fast failures / reloads where
          // `updating: true` was never observed on this subscription).
          if (s.updating || this.isCurrentRunTerminal(s)) {
            wasUpdating = true;
          }
          const upd = s.updGlobal;
          // A terminal snapshot (e.g. status 7 / progress 100, updating=false)
          // OLDER than our launch is leftover state from a previous run: it must
          // neither drive the progress UI (fake "complete 100%") nor finish the
          // phase early.
          const isStaleTerminal = !wasUpdating && !!upd && TERMINAL_UPD_STATUSES.includes(upd.status);
          if (isStaleTerminal) {
            Logger.state('stale terminal upd_global ignored ' + JSON.stringify(upd));
          }
          if (upd && !isStaleTerminal) {
            this.progressStatus = upd.status;
            this.progressValue = upd.progress ?? 0;
          }
          if (!wasUpdating) {
            this.cdr.markForCheck();
            return;
          }
          if (upd?.status === 7) {
            this.finishOneClick('success');
            return;
          }
          if (upd?.status === 2 || upd?.status === 4 || upd?.status === 6) {
            this.finishOneClick('failed');
            return;
          }
          this.cdr.markForCheck();
        },
        error: () => this.finishOneClick('failed'),
      });

    // Pull fallback: the api path creates a Solarman ORDER, so lastAction can
    // arbitrate when the push channel goes silent — subscription dropped
    // during the logger reboots, or terminal state reached before this page
    // load with no further pushes coming (the cloud only pushes on changes).
    // Polled sparingly and only during silence; the store stays authoritative.
    if (this.solarmanDeviceId == null) {
      return;
    }
    this.lastActionPollSub = timer(ONE_CLICK_LASTACTION_POLL_MS, ONE_CLICK_LASTACTION_POLL_MS)
      .pipe(
        filter(() => new Date().getTime() - lastPushAt >= ONE_CLICK_PUSH_SILENCE_MS),
        exhaustMap(() => from(this.oneClickUpgradeService.getLastAction(this.solarmanDeviceId!))),
        takeUntil(this.destroy$),
      )
      .subscribe((lastAction) => this.applyLastActionOutcome(lastAction));
  }

  /**
   * Interpret a lastAction snapshot as the one-click outcome. Orders older
   * than the current launch are ignored (the endpoint returns the LAST order
   * ever — it could belong to a previous run). status 666 = success;
   * error/500/400 or expired without success = failed; still inside its
   * order timeout = keep waiting on the push channel.
   */
  private applyLastActionOutcome(lastAction: CallPROSolarmanLastActionResponse | null): void {
    if (!lastAction || this.phase !== 'updating') {
      return;
    }
    const startTs = this.updateStartTs ?? this.getOptimisticStartTs();
    if (startTs != null && (lastAction.createTime ?? 0) < startTs - 60_000) {
      Logger.state('lastAction fallback: stale order ignored ' + JSON.stringify({ createTime: lastAction.createTime, startTs }));
      return;
    }
    if (isLoggerUpgrading$(lastAction)) {
      Logger.state('lastAction fallback: order still in progress ' + JSON.stringify({ status: lastAction.status }));
      return;
    }
    if (lastAction.status === 666) {
      Logger.state('lastAction fallback: order completed (666)');
      this.finishOneClick('success');
      return;
    }
    Logger.warn('lastAction fallback: order ended without success ' + JSON.stringify({ status: lastAction.status, error: lastAction.error }));
    this.finishOneClick('failed');
  }

  private finishOneClick(result: 'success' | 'failed'): void {
    this.progressSub?.unsubscribe();
    this.lastActionPollSub?.unsubscribe();
    this.lastActionPollSub = null;
    this.updateStartTs = null;
    localStorage.removeItem(ONE_CLICK_STORAGE_KEY(this.inverterSn));
    this.setPhase(result);
    if (result === 'success') {
      this.toast(this.labels.oneClickSuccess, 'success');
      this.refreshRequested.emit();
    } else {
      this.toast(this.labels.oneClickFailed, 'error');
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────

  private toast(message: string, severity: 'success' | 'error'): void {
    this.toastFacade.present(message, '', { severity, browserMode: 'simple' });
  }

  private async confirm(content: string): Promise<boolean> {
    const title = this.labels.confirmTitle;
    if (this.uiFramework === 'material') {
      const dialogRef = this.dialog.open(DialogComponent, { width: '600px' });
      dialogRef.componentInstance.name = title;
      dialogRef.componentInstance.description = content;
      dialogRef.componentInstance.primary = { name: this.labels.confirm, click: () => dialogRef.close(true) };
      dialogRef.componentInstance.secondary = { name: this.labels.cancel, click: () => dialogRef.close(false) };
      return (await dialogRef.afterClosed().pipe(first()).toPromise()) === true;
    }
    const popover = await this.popoverController.create({
      component: DialogComponent,
      componentProps: {
        name: title,
        description: content,
        primary: { name: this.labels.confirm, click: () => popover.dismiss(true) },
        secondary: { name: this.labels.cancel, click: () => popover.dismiss(false) },
      },
      cssClass: 'confirm-popover',
      backdropDismiss: false,
    });
    await popover.present();
    const { data } = await popover.onDidDismiss();
    return data === true;
  }
}
